import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Profiles core helpers
 * - UI agnostic
 * - No fallbacks: surface errors immediately per development policy
 */

export type UserProfileCore = {
    id?: string;
    user_id: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    phone?: string | null;
    company?: string | null;
    role?: string | null;
    notification_preferences?: any;
    preferred_vault_id?: string | null;
    claimed_contact_id?: string | null;
    timezone?: string | null;
    start_view?: string | null;
    updated_at?: string | null;
};

export async function getUserProfileCore(sb: SupabaseClient, userId: string): Promise<UserProfileCore | null> {
    if (!userId) throw new Error('getUserProfileCore: userId is required');
    const { data, error } = await sb
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
    if (error) throw error;
    return (data as any) || null;
}

export async function upsertUserProfileCore(sb: SupabaseClient, userId: string, patch: Partial<UserProfileCore>) {
    if (!userId) throw new Error('upsertUserProfileCore: userId is required');
    const base = { user_id: userId, updated_at: new Date().toISOString(), ...patch } as any;
    // Determine if row exists
    const { data: existing } = await sb
        .from('user_profiles')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();
    if (existing) {
        const { error } = await sb.from('user_profiles').update(base).eq('user_id', userId);
        if (error) throw error;
    } else {
        const { error } = await sb.from('user_profiles').insert(base);
        if (error) throw error;
    }
}

export async function getClaimedContactIdCore(sb: SupabaseClient, userId: string): Promise<string | null> {
    if (!userId) throw new Error('getClaimedContactIdCore: userId is required');
    const { data, error } = await sb
        .from('user_profiles')
        .select('claimed_contact_id')
        .eq('user_id', userId)
        .maybeSingle();
    if (error) throw error;
    return ((data as any)?.claimed_contact_id as string) || null;
}

export async function getPreferredVaultIdCore(sb: SupabaseClient, userId: string): Promise<string | null> {
    if (!userId) throw new Error('getPreferredVaultIdCore: userId is required');
    const { data, error } = await sb
        .from('user_profiles')
        .select('preferred_vault_id')
        .eq('user_id', userId)
        .maybeSingle();
    if (error) throw error;
    return ((data as any)?.preferred_vault_id as string) || null;
}

export async function setPreferredVaultIdCore(sb: SupabaseClient, userId: string, vaultId: string) {
    if (!userId) throw new Error('setPreferredVaultIdCore: userId is required');
    if (!vaultId) throw new Error('setPreferredVaultIdCore: vaultId is required');
    await upsertUserProfileCore(sb, userId, { preferred_vault_id: vaultId });
}

export async function getTimezoneCore(sb: SupabaseClient, userId: string): Promise<string | null> {
    if (!userId) throw new Error('getTimezoneCore: userId is required');
    const { data, error } = await sb
        .from('user_profiles')
        .select('timezone')
        .eq('user_id', userId)
        .maybeSingle();
    if (error) throw error;
    return ((data as any)?.timezone as string) || null;
}

export async function setTimezoneCore(sb: SupabaseClient, userId: string, timezone: string) {
    if (!userId) throw new Error('setTimezoneCore: userId is required');
    await upsertUserProfileCore(sb, userId, { timezone });
}

export async function getStartViewCore(sb: SupabaseClient, userId: string): Promise<string | null> {
    if (!userId) throw new Error('getStartViewCore: userId is required');
    const { data, error } = await sb
        .from('user_profiles')
        .select('start_view')
        .eq('user_id', userId)
        .maybeSingle();
    if (error) throw error;
    return ((data as any)?.start_view as string) || null;
}

export async function setStartViewCore(sb: SupabaseClient, userId: string, startView: string) {
    if (!userId) throw new Error('setStartViewCore: userId is required');
    await upsertUserProfileCore(sb, userId, { start_view: startView });
}

// New: central helper to set the user's claimed_contact_id
export async function setClaimedContactIdCore(sb: SupabaseClient, userId: string, contactId: string) {
    if (!userId) throw new Error('setClaimedContactIdCore: userId is required');
    if (!contactId) throw new Error('setClaimedContactIdCore: contactId is required');
    await upsertUserProfileCore(sb, userId, { claimed_contact_id: contactId });
}

// Backwards-compatible aliases (naming without the Id suffix)
export const getPreferredVaultCore = getPreferredVaultIdCore;
export const setPreferredVaultCore = setPreferredVaultIdCore;
